import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useCostCurrency } from '../hooks/useCostCurrency'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import ConfirmDialog from '../../schedule/components/ConfirmDialog'
import RecordAttachmentsCard from '../components/RecordAttachmentsCard'
import RecordHistoryCard from '../components/RecordHistoryCard'
import { useChangeMutations } from '../hooks/useChangeMutations'
import { changeAffordances, changeSubmitBlocker } from '../lib/changes'
import { describeCostControlError, isRecordGoneError } from '../lib/errors'
import { moneyLocale } from '../lib/money'
import { recordDetailState } from '../lib/records'
import { buildTree } from '../lib/tree'
import type { CostChangeDetail, CostContract, CostControlView } from '../types'
import ChangeDetailSkeleton from './ChangeDetailSkeleton'
import ChangeDialog from './ChangeDialog'
import ConvertContractDialog from './ConvertContractDialog'
import ChangeHeader from './ChangeHeader'
import ChangeLinesCard from './ChangeLinesCard'
import ChangeLinesDialog from './ChangeLinesDialog'
import ChangeSummaryCard from './ChangeSummaryCard'
import RejectDialog from './RejectDialog'

const NOT_FOUND = 'CostControl_error_change_not_found'
type Pending = 'submit' | 'approve' | 'reopen' | 'unapprove' | 'delete' | 'convertBudget' | 'createRfq' | null
const CONFIRM: Record<Exclude<Pending, null>, { title: string; text: string; label: string }> = {
  submit: { title: 'CostControl_change_submit_title', text: 'CostControl_change_submit_text', label: 'CostControl_change_submit' },
  approve: { title: 'CostControl_change_approve_title', text: 'CostControl_change_approve_text', label: 'CostControl_change_approve' },
  reopen: { title: 'CostControl_change_reopen_title', text: 'CostControl_change_reopen_text', label: 'CostControl_change_reopen' },
  unapprove: { title: 'CostControl_change_unapprove_title', text: 'CostControl_change_unapprove_text', label: 'CostControl_change_unapprove' },
  delete: { title: 'CostControl_change_delete_title', text: 'CostControl_change_delete_text', label: 'Common_delete' },
  convertBudget: { title: 'CostControl_potential_convert_budget', text: 'CostControl_potential_convert_budget_text', label: 'CostControl_potential_convert' },
  createRfq: { title: 'CostControl_potential_create_rfq', text: 'CostControl_potential_create_rfq_text', label: 'CostControl_potential_create_rfq' },
}

const ChangeDetail: React.FC = () => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { match, history } = useRouter()
  const { projectId, changeId } = match.params as { projectId: string; changeId: string }
  const actions = useModuleActions('COST_CONTROL', projectId)
  const docControl = useModuleActions('DOC_CONTROL', projectId)
  const { data, loading, error } = useQuery<{ costChange: CostChangeDetail }>(Query.COST_CHANGE, {
    variables: { id: changeId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  // The item tree for the lines editor; cache-and-network so a budget edited meanwhile shows (3a deferred item).
  const { data: viewData } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    skip: !actions.canView,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const currency = useCostCurrency(projectId)
  const { data: contractData, loading: contractsLoading, error: contractsError } = useQuery<{ costContracts: CostContract[] }>(Query.COST_CONTRACTS, {
    variables: { projectId, filter: {} },
    skip: !actions.canView,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  // A write's reload of this change can be refused (someone deleted it); the page's own query never hears of it.
  const [lost, setLost] = useState<{ id: string; message: string } | null>(null)
  const onDetailError = useCallback((id: string, e: unknown) => {
    const message = (e as { message?: string } | null)?.message ?? ''
    if (isRecordGoneError(message, NOT_FOUND)) setLost({ id, message })
  }, [])
  const mutations = useChangeMutations(projectId, onDetailError)
  const [editing, setEditing] = useState(false)
  const [linesOpen, setLinesOpen] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [pending, setPending] = useState<Pending>(null)
  const [convertingContract, setConvertingContract] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const tree = useMemo(() => buildTree(viewData?.costControl.nodes ?? []), [viewData])
  const detail = data?.costChange
  const message = (lost && lost.id === changeId ? lost.message : null) ?? error?.message ?? null
  const state = recordDetailState({
    loading, hasData: !!detail, recordProjectId: detail?.change.projectId ?? null, projectId, message,
    rightsLoading: actions.loading, canView: actions.canView, notFoundKey: NOT_FOUND,
  })
  const af = changeAffordances(actions, state === 'ready' && detail ? detail.change : null)
  // A dialog closes as soon as its action is no longer allowed: someone else moved the change on (3a deferred item).
  useEffect(() => { if (!af.editHeader) setEditing(false) }, [af.editHeader])
  useEffect(() => { if (!af.editLines) setLinesOpen(false) }, [af.editLines])
  useEffect(() => { if (!af.reject) setRejecting(false) }, [af.reject])
  useEffect(() => { if (!af.convert) setConvertingContract(false) }, [af.convert])
  useEffect(() => { if (!af.dismiss) setDismissing(false) }, [af.dismiss])
  const back = () => history.push(detail?.access === 'REPRESENTATIVE' && detail.change.contractId
    ? `/project/${projectId}/cost-control/contracts/${detail.change.contractId}`
    : `/project/${projectId}/cost-control?tab=changes`)

  if (state === 'loading') return <ChangeDetailSkeleton />
  if (state !== 'ready' || !detail) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={back}>
          <ArrowLeft className="h-4 w-4" />
          {t('CostControl_change_back')}
        </Button>
        <p className="text-sm text-muted-foreground">{state === 'error' ? describeCostControlError(message, t) : t(NOT_FOUND)}</p>
      </div>
    )
  }

  const { change } = detail
  const contracts = contractData?.costContracts ?? []
  const confirmText = pending ? CONFIRM[pending] : null

  const confirm = async () => {
    if (!pending) return
    if (pending === 'delete') {
      const res = await mutations.remove(change.id)
      setPending(null)
      if (res.ok) {
        toastr.success('', t('CostControl_change_deleted'))
        back()
      }
      return
    }
    if (pending === 'createRfq') {
      const res = await mutations.createRfq(change.id)
      setPending(null)
      if (res.ok) history.push(`/project/${projectId}/cost-control/rfqs/${res.data.createCostRfqFromPotentialChange.id}`)
      return
    }
    const step = {
      submit: mutations.submit, approve: mutations.approve, reopen: mutations.reopen, unapprove: mutations.unapprove,
      convertBudget: (id: string) => mutations.convert(id, 'BUDGET_CHANGE', null),
    }[pending]
    await step(change.id)
    setPending(null)
  }

  return (
    <div className="space-y-4">
      <ChangeHeader
        change={change}
        af={af}
        blocker={changeSubmitBlocker(change)}
        onBack={back}
        onEdit={() => setEditing(true)}
        onSubmit={() => setPending('submit')}
        onApprove={() => setPending('approve')}
        onReject={() => setRejecting(true)}
        onReopen={() => setPending('reopen')}
        onUnapprove={() => setPending('unapprove')}
        onDelete={() => setPending('delete')}
        onConvertBudget={() => setPending('convertBudget')}
        onConvertContract={() => setConvertingContract(true)}
        onCreateRfq={() => setPending('createRfq')}
        onDismiss={() => setDismissing(true)}
      />
      <ChangeSummaryCard change={change} projectId={projectId} currency={currency} locale={locale} />
      <ChangeLinesCard change={change} lines={detail.lines} currency={currency} locale={locale} canEdit={af.editLines} onEdit={() => setLinesOpen(true)} />
      <div className="grid gap-4 lg:grid-cols-2">
        <RecordAttachmentsCard
          projectId={projectId}
          attachments={detail.attachments}
          canAdd={af.addAttachment}
          canRemove={af.removeAttachment}
          canLinkDocuments={docControl.canView}
          onAdd={(source) => mutations.addAttachment(change.id, source)}
          onRemove={(attachmentId) => mutations.removeAttachment(change.id, attachmentId)}
        />
        <RecordHistoryCard projectId={projectId} entityType="CHANGE" entityId={change.id} currency={currency} locale={locale} />
      </div>
      <ChangeDialog open={editing} mode="edit" change={change} contracts={contracts} onOpenChange={setEditing} onSubmit={async (input) => (await mutations.update(change.id, input)).ok} />
      <ChangeLinesDialog
        open={linesOpen}
        change={change}
        lines={detail.lines}
        tree={tree}
        currency={currency}
        locale={locale}
        onOpenChange={setLinesOpen}
        onSave={async (lines) => (await mutations.setLines(change.id, lines)).ok}
      />
      <RejectDialog open={rejecting} onOpenChange={setRejecting} onReject={async (note) => (await mutations.reject(change.id, note)).ok} />
      <ConvertContractDialog
        open={convertingContract}
        change={change}
        contracts={contracts}
        contractsState={contractData ? 'ready' : contractsError ? 'error' : contractsLoading ? 'loading' : 'ready'}
        lines={detail.lines}
        rows={viewData?.costControl.nodes ?? []}
        onOpenChange={setConvertingContract}
        onConvert={async (contractId) => (await mutations.convert(change.id, 'CONTRACT_CHANGE', contractId)).ok}
      />
      <RejectDialog
        open={dismissing}
        onOpenChange={setDismissing}
        onReject={async (note) => (await mutations.dismiss(change.id, note)).ok}
        titleKey="CostControl_potential_dismiss"
        hintKey="CostControl_potential_dismiss_hint"
        noteKey="CostControl_potential_dismiss_note"
        actionKey="CostControl_potential_dismiss"
      />
      <ConfirmDialog
        open={!!pending}
        title={confirmText ? t(confirmText.title) : ''}
        description={confirmText ? t(confirmText.text) : undefined}
        confirmLabel={confirmText ? t(confirmText.label) : ''}
        variant={pending === 'delete' ? 'destructive' : 'default'}
        submitting={mutations.saving}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      />
    </div>
  )
}
export default ChangeDetail
